import { DecimalPipe } from '@angular/common';
import {
  Directive,
  HostListener,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';

@Directive({
  standalone: true,
  selector: 'input[opsNoiceNumber]',
  providers: [DecimalPipe],
})
export class OpsNoiceNumberDirective implements AfterViewChecked {
  inputRef: HTMLInputElement;

  // Allow decimal numbers. The \. is only allowed once to occur
  private regex = new RegExp(/^[0-9]+(\.[0-9]*){0,1}$/g);

  //control key state
  isCtrlPressed = false;

  // Allow key codes for special events. Reflect :
  // Backspace, tab, end, home
  private specialKeys: Array<string> = [
    'Backspace',
    'Delete',
    'Tab',
    'End',
    'Home',
    'ArrowRight',
    'ArrowDown',
    'ArrowLeft',
    'ArrowUp',
    '.',
  ];

  constructor(elementRef: ElementRef, private decimalPipe: DecimalPipe) {
    this.inputRef = elementRef.nativeElement;
  }

  ngAfterViewChecked() {
    if (!this.inputRef.value) {
      return;
    }

    if (isNaN(+this.inputRef.value)) {
      return;
    }

    const lastCharIndex = this.inputRef.value.length - 1;

    if (this.inputRef.value[this.inputRef.value.length - 1] === '.') {
      return;
    }

    this.inputRef.value =
      this.decimalPipe
        .transform(
          +this.inputRef.value.toString().split(',').join(''),
          '.0-2',
          'en-US'
        )
        ?.toString() ?? '';
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any) {
    //#region NumberOnly
    this.isCtrlPressed = false;

    //prevent double decimal separator
    if (event.key === '.' && event.target.value.includes('.')) {
      event.preventDefault();
      return;
    }

    if (event.key === ',') {
      event.preventDefault();
      return;
    }

    //allow and track ctrl, command and windows keys
    if (['Control', 'Meta'].includes(event.key)) {
      this.isCtrlPressed = true;
    }
    //allow ctrl actions/shortcuts (copy, paste, select all, cut,...)
    if (this.isCtrlPressed) {
      return;
    }

    // Do not use event.keycode this is deprecated.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
    if (!this.specialKeys.includes(event.key)) {
      const current: string = this.inputRef.value;
      // We need this because the current value on the DOM element
      // is not yet updated with the value from this event
      const next: string = current
        ? current?.concat(event.key).replaceAll(/(,)/gi, '')
        : '';
      if (next && !String(next).match(this.regex)) {
        event?.preventDefault();
      }
    } else {
      if (
        ['Backspace'].includes(event.key) &&
        event.target.value[event.target['selectionStart'] - 1] === ','
      ) {
        const preStr = event.target.value.slice(
          0,
          event.target['selectionStart'] - 2
        );

        const cleanup = preStr.replace(',', '');
        const cleanupValLen = cleanup.length;

        const vvalue =
          event.target.value.slice(0, event.target['selectionStart'] - 2) +
          event.target.value.slice(event.target['selectionStart']);

        const fvalue = this.decimalPipe.transform(
          +vvalue.toString().replaceAll(',', ''),
          '.0-2',
          'en-US'
        );

        const preStr2 = event.target.value.split('');
        let newPoint = 0;
        let countMark = 0;
        for (let i = 0, l = preStr2.length, s = 0; i < l; i++) {
          if (preStr2[i] != ',') {
            s += 1;
            if (cleanupValLen >= s) {
              newPoint = s + countMark;
            }
          } else {
            if (cleanupValLen >= s) {
              countMark += 1;
            }
          }
        }

        event.target.value = fvalue;
        this.inputRef.value = fvalue;
        this.inputRef.setSelectionRange(newPoint, newPoint);

        event?.preventDefault();

        // this.inputRef.value =
        //   this.inputRef.value.slice(0, event.target['selectionStart'] - 2) +
        //   this.inputRef.value.slice(event.target['selectionStart']);

        // this.inputRef.value = this.decimalPipe.transform(
        //   +this.inputRef.value.toString().replaceAll(',', ''),
        //   '.0-2',
        //   'en-US'
        // );

        // event?.preventDefault();
      }

      if (
        ['Delete'].includes(event.key) &&
        event.target.value[event.target['selectionStart']] === ','
      ) {
        const preStr = event.target.value.slice(
          0,
          event.target['selectionStart']
        );

        const cleanup = preStr.replace(',', '');
        const cleanupValLen = cleanup.length;

        const vvalue =
          event.target.value.slice(0, event.target['selectionStart']) +
          event.target.value.slice(event.target['selectionStart'] + 2);

        const fvalue = this.decimalPipe.transform(
          +vvalue.toString().replaceAll(',', ''),
          '.0-2',
          'en-US'
        );

        const preStr2 = event.target.value.split('');
        let newPoint = 0;
        let countMark = 0;
        for (let i = 0, l = preStr2.length, s = 0; i < l; i++) {
          if (preStr2[i] != ',') {
            s += 1;
            if (cleanupValLen >= s) {
              newPoint = s + countMark;
            }
          } else {
            if (cleanupValLen >= s) {
              countMark += 1;
            }
          }
        }

        event.target.value = fvalue;
        this.inputRef.value = fvalue;
        this.inputRef.setSelectionRange(newPoint, newPoint);

        event?.preventDefault();
      }
    }

    //#endregion NumberOnly
  }
  @HostListener('input', ['$event'])
  onInput(event: any) {
    const targetAttributes = event.target.attributes as NamedNodeMap;
    if (
      !targetAttributes.getNamedItem('nopsnumber') &&
      !targetAttributes.getNamedItem('opsnoicenumber')
    ) {
      return;
    }

    //m4 cashflow description field input gets caught by this listener even if the directive isnt applied, this is a workaround
    // TODO: find out another way to prevent this unwanted behaviour
    const target = event.target;
    if (!target.value) {
      return;
    }

    const lastCharIndex = target.value.length - 1;

    if (target.value[lastCharIndex] === '.') {
      if (target.value[lastCharIndex - 1] === '.') {
        target.value = target.value.slice(0, -1);
        target.value;
      }
      return;
    }

    const posStart = target.selectionStart;
    const oldSeparatorCount = target.value.toString().split(',').length - 1;

    this.inputRef.value = this.decimalPipe.transform(
      +this.inputRef.value.toString().replaceAll(',', ''),
      '.0-2',
      'en-US'
    );

    // if (
    //   !event.data &&
    //   event.inputType === 'deleteContentBackward' &&
    //   this.inputRef.value.charAt(posStart) === ','
    // ) {
    //   //    console.log(this.inputRef.value)
    //   // this.inputRef.value =
    //   //   this.inputRef.value.slice(0, posStart - 1) +
    //   //   this.inputRef.value.slice(posStart);
    //   posStart--;
    //   // contentSeparator.push(true);
    // }
    // //    console.log(this.inputRef.value.charAt(posStart))
    // if (
    //   !event.data &&
    //   event.inputType === 'deleteContentForward' &&
    //   this.inputRef.value.charAt(posStart) === ','
    // ) {
    //   // this.inputRef.value =
    //   //   this.inputRef.value.slice(0, posStart + 1) +
    //   //   this.inputRef.value.slice(posStart + 2);
    //   posStart++;
    //   // contentSeparator.push(true);
    // }

    this.inputRef.value = this.decimalPipe.transform(
      +this.inputRef.value.toString().replaceAll(',', ''),
      '.0-2',
      'en-US'
    );

    const newSeparatorCount = target.value.toString().split(',').length - 1;
    const offset = newSeparatorCount - oldSeparatorCount;

    if (posStart + offset <= 0) {
      this.inputRef.setSelectionRange(0, 0);
    } else {
      this.inputRef.setSelectionRange(posStart + offset, posStart + offset);
    }
  }
}
